import { BaseModule } from "by/gamefactory/cityversetycoon/composition/module/BaseModule";
import { MarketplaceScene, MarketplaceSceneOptions } from "./main/MarketplaceScene";
import { MarketplaceController } from "./main/models/controller/MarketplaceController";
import { ParamDescription } from "by/gamefactory/cityversetycoon/composition/module/ParamDescription";
import { MarketplaceModuleFlowExecutor } from "./MarketplaceModuleFlowExecutor";
import { MarketplaceViewModel } from "./main/models/MarketplaceViewModel";
import { MarketplaceMapModel } from "./main/models/map/MarketplaceMapModel";
import { MarketplaceBuildingModel } from "./main/models/building/MarketplaceBuildingModel";
import { MarketplaceNewsModel } from "./main/models/news/MarketplaceNewsModel";
import { RemoveBuildingPopupScene } from "./removebuilding/RemoveBuildingPopupScene";
import { ShowRemoveSceneParams } from "./removebuilding/ShowRemoveSceneParams";
import { RemoveBuildingController } from "./removebuilding/controllers/RemoveBuildingController";
import { RemoveBuildingModel } from "./removebuilding/models/RemoveBuildingModel";
import { MarketplaceBuildingItem } from "./main/models/building/MarketplaceBuildingItem";
import { UseDiscountPopupScene } from "./usediscount/UseDiscountPopupScene";
import { UseDiscountController } from "./usediscount/controllers/UseDiscountController";
import { UseDiscountViewModel } from "./usediscount/model/UseDiscountVIewModel";
import { BenefitsModel } from "../../common/models/benefits/BenefitsModel";
import { MyDiscountPopupScene } from "./mydiscount/MyDiscountPopupScene";
import { CancellationToken } from "async/CancellationToken";
import { MarketplaceResourcePackage } from "./resources/MarketplaceResourcePackage";
import { PriceHistoryPopupScene } from "../pricehistory/PriceHistoryPopupScene";
import { PurchaseHistory } from "./main/models/building/PurchaseHistory";
import { PriceHistoryResourcePackage } from "../pricehistory/resources/PriceHistoryResourcePackage";
import { EditPriceModule } from "../editprice/EditPriceModule";
import { Lifecycle } from "@launchtray/tsyringe-async";

export class MarketplaceModule extends BaseModule<void> {
    private marketplaceResources: MarketplaceResourcePackage;
    private priceHistoryResourcePackage: PriceHistoryResourcePackage;

    public override async build(): Promise<void> {
        this.internalContainer.register(MarketplaceModuleFlowExecutor, MarketplaceModuleFlowExecutor);

        this.internalContainer.register(MarketplaceScene, MarketplaceScene);
        this.internalContainer.register("IMarketplaceController", MarketplaceController);
        this.internalContainer.register("MarketplaceSceneFactory", {
            useValue: async (options: MarketplaceSceneOptions) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    MarketplaceScene,
                    new ParamDescription("MarketplaceSceneOptions", options)
                );
            }
        });

        this.registerModule(EditPriceModule);

        this.internalContainer.registerSingleton("IMarketplaceViewModel", MarketplaceViewModel);
        this.internalContainer.registerSingleton("IMarketplaceMapModel", MarketplaceMapModel);
        this.internalContainer.registerSingleton("IMarketplaceBuildingModel", MarketplaceBuildingModel);
        this.internalContainer.registerSingleton("IMarketplaceNewsModel", MarketplaceNewsModel);

        this.marketplaceResources = new MarketplaceResourcePackage();
        await this.marketplaceResources.load();
        this.internalContainer.registerInstance(MarketplaceResourcePackage, this.marketplaceResources);

        this.internalContainer.registerSingleton("IRemoveBuildingModel", RemoveBuildingModel);
        this.internalContainer.register("IRemoveBuildingController", RemoveBuildingController);
        this.internalContainer.register(RemoveBuildingPopupScene, RemoveBuildingPopupScene);
        this.internalContainer.register("RemoveBuildingPopupSceneFactory", {
            useValue: async (showRemoveSceneParams: ShowRemoveSceneParams) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    RemoveBuildingPopupScene,
                    new ParamDescription(ShowRemoveSceneParams, showRemoveSceneParams)
                );
            }
        });

        this.internalContainer.register(MyDiscountPopupScene, MyDiscountPopupScene);
        this.internalContainer.register("MyDiscountPopupSceneFactory", {
            useValue: async (cancellationToken: CancellationToken) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    MyDiscountPopupScene,
                    new ParamDescription(CancellationToken, cancellationToken)
                );
            }
        });

        this.internalContainer.registerSingleton("IBenefitsModel", BenefitsModel);
        this.internalContainer.register("IUseDiscountViewModel", UseDiscountViewModel, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register("IUseDiscountController", UseDiscountController);
        this.internalContainer.register(UseDiscountPopupScene, UseDiscountPopupScene);
        this.internalContainer.register("UseDiscountPopupSceneFactory", {
            useValue: async (currentMpBuildingItem: MarketplaceBuildingItem, cancellationToken: CancellationToken) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    UseDiscountPopupScene,
                    new ParamDescription(MarketplaceBuildingItem, currentMpBuildingItem),
                    new ParamDescription(CancellationToken, cancellationToken)
                );
            }
        });

        this.priceHistoryResourcePackage = new PriceHistoryResourcePackage();
        await this.priceHistoryResourcePackage.load();
        this.internalContainer.registerInstance(PriceHistoryResourcePackage, this.priceHistoryResourcePackage);
        this.internalContainer.register(PriceHistoryPopupScene, PriceHistoryPopupScene);
        this.internalContainer.register("PriceHistoryPopupSceneFactory", {
            useValue: async (priceHistory: Array<PurchaseHistory>) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    PriceHistoryPopupScene,
                    new ParamDescription(Array<PurchaseHistory>, priceHistory)
                );
            }
        });

        await super.build();
    }

    public async start(buildingArea?: number): Promise<void> {
        const executor = await this.internalContainer.resolve(MarketplaceModuleFlowExecutor);
        await executor.execute(this, buildingArea);
        this.dispose();
    }

    public override dispose(): void {
        this.marketplaceResources.destroy();
        this.priceHistoryResourcePackage.destroy();
        super.dispose();
    }
}
